/**
 * OfferAIAssessmentButton — small instructor-facing CTA that opens
 * the OfferAIAssessmentModal (Track A, Phase D, Feb 13, 2026).
 *
 * Renders nothing if `pupilId` is missing (i.e. pupil isn't linked
 * to a platform account yet — no user to send the offer to).
 *
 * Media-quarantine doctrine (Feb 27, 2026): the underlying modal
 * returns null when `MEDIA_FEATURE_ENABLED=false`. Without a guard
 * on THIS button the operator sees a clickable CTA that opens
 * nothing — exactly the "dead old upload button" UX we are
 * eliminating. We therefore hide the trigger too when the flag
 * is off.
 *
 * Designed to slot next to PupilDriveBriefBadge / MindsetBriefCard in
 * Work Mode pre-lesson. Calm; pupil-aware; no monetary noise here
 * (price + margin live inside the modal).
 */
import { useState } from "react";
import { Sparkles } from "lucide-react";

import OfferAIAssessmentModal from "./OfferAIAssessmentModal";
import { MEDIA_FEATURE_ENABLED } from "../../lib/mediaFeatureFlag";


export default function OfferAIAssessmentButton({ pupilId, pupilName }) {
  const [open, setOpen] = useState(false);
  if (!pupilId) return null;
  // Quarantine — the modal is inert while the flag is off, so the
  // trigger must vanish too. No ghost buttons.
  if (!MEDIA_FEATURE_ENABLED) return null;
  return (
    <>
      <button
        type="button"
        data-testid="offer-ai-assessment-open-button"
        onClick={() => setOpen(true)}
        className="w-full rounded-2xl border border-indigo-700/40 bg-indigo-900/10 hover:bg-indigo-900/20 px-4 py-3 flex items-center gap-3 active:scale-[0.99] transition"
      >
        <div className="w-9 h-9 rounded-xl bg-indigo-500/20 border border-indigo-500/40 flex items-center justify-center flex-shrink-0">
          <Sparkles className="w-4 h-4 text-indigo-300" />
        </div>
        <div className="flex-1 text-left">
          <p className="text-[13px] font-extrabold text-white leading-tight">
            Offer AI assessment
          </p>
          <p className="text-[11.5px] text-indigo-200/70 leading-snug mt-0.5">
            One-off AI review for {pupilName || "this pupil"} — you set the price.
          </p>
        </div>
      </button>
      <OfferAIAssessmentModal
        open={open}
        pupilId={pupilId}
        pupilName={pupilName}
        onClose={() => setOpen(false)}
      />
    </>
  );
}
